import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, Mail, BookOpen, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EnrollmentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setError('No payment session found');
      setLoading(false);
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payments/checkout/status/${sessionId}`);
      setStatus(response.data);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setError('Unable to verify payment status. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-emerald-600 animate-spin mx-auto" />
          <p className="mt-4 text-gray-600 text-lg">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Something Went Wrong</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <a 
            href="mailto:support@growingminds.co.uk" 
            className="text-emerald-600 hover:text-emerald-700 font-medium"
          >
            Contact Support
          </a>
        </div>
      </div>
    );
  }

  if (status?.payment_status !== 'paid') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <Loader2 className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Payment Processing</h1>
          <p className="text-gray-600 mb-6">
            Your payment is being processed. This page will update automatically once complete.
          </p>
          <button
            onClick={checkPaymentStatus}
            className="bg-amber-500 text-white px-6 py-2 rounded-lg hover:bg-amber-600 transition-colors"
          >
            Check Status Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-14 h-14 text-emerald-500" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Payment Successful!</h1>
          <p className="text-gray-600">Welcome to Growing Minds Tuition</p>
        </div>

        {/* Payment Details */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-6">
          <div className="flex justify-between items-center">
            <span className="text-emerald-700 font-medium">Amount Paid</span>
            <span className="text-2xl font-bold text-emerald-800">
              £{status?.amount || 0}
            </span>
          </div>
        </div>

        {/* What's Next */}
        <div className="space-y-4 mb-8">
          <h2 className="font-semibold text-gray-800 text-lg">What happens next?</h2>
          
          <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl">
            <Mail className="w-6 h-6 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800">Check your email</p>
              <p className="text-blue-600 text-sm">
                We've sent login credentials for your child's account to your email address.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-xl">
            <BookOpen className="w-6 h-6 text-purple-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-purple-800">Start Learning</p>
              <p className="text-purple-600 text-sm">
                Your child can now log in and start their personalised learning journey!
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <Link
            to="/login"
            className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:from-emerald-700 hover:to-teal-700 transition-all"
          >
            Go to Login
            <ArrowRight className="w-5 h-5" />
          </Link>
          
          <Link
            to="/"
            className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-medium flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            Back to Home
          </Link>
        </div>

        {/* Support */}
        <p className="text-center text-gray-500 text-sm mt-6">
          Questions? Contact us at{' '}
          <a href="mailto:support@growingminds.co.uk" className="text-emerald-600 hover:underline">
            support@growingminds.co.uk
          </a>
        </p>
      </div>
    </div>
  );
};

export default EnrollmentSuccessPage;
